import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Progress } from "../components/ui/progress";
import {
  Plus,
  Search,
  FileText,
  Users,
  Calendar,
  CheckCircle,
  AlertCircle,
  Clock,
  Building2,
  UserPlus
} from "lucide-react";
import { Policy, Memo, Department } from "../types";
import { toSydneyDate } from "../utils/helpers";
import { Can } from "../components/Can";
import { ACTIONS, RESOURCES } from "../constants/permissions.constants";

// (Interfaces like CombinedAssignmentWithDetails, GroupedAssignment remain the same)
interface CombinedAssignmentWithDetails {
  id: string;
  type: 'Policy' | 'Memo';
  item: {
    id: string;
    title?: string;
    subject?: string;
    version?: number;
    number?: string;
    status: string;
    requireSignature: boolean;
    effectiveDate: string;
  };
  user: {
    id: string;
    name: string;
    email: string;
    department: Department;
    jobRole: string;
  };
  assignedDate: string;
  readDate?: string;
  remindersSent: number;
  status: string;
  signedDate?: string;
}

interface GroupedAssignment {
  id: string;
  type: 'Policy' | 'Memo';
  name: string;
  version?: number;
  number?: string;
  requireSignature: boolean;
  effectiveDate: string;
  documentStatus: string;
  assignments: CombinedAssignmentWithDetails[];
  stats: {
    totalAssigned: number;
    totalRead: number;
    totalSigned: number;
    completionPercentage: number;
    overallStatus: 'Not Started' | 'In Progress' | 'Complete' | 'Overdue';
  };
  departments: Department[];
  latestAssignedDate: string;
}


export default function PoliciesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const {
    policies,
    memos,
    departments,
    policyAssignments,
    memoAssignments,
    getAllPolicyAssignments,
    refreshPolicies,
    refreshMemos
  } = useData();

  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || "policies");
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    navigate(`/policies?tab=${value}`, { replace: true });
  };

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [memoSearchTerm, setMemoSearchTerm] = useState("");
  const [memoStatusFilter, setMemoStatusFilter] = useState<string>("all");
  const [assignmentSearchTerm, setAssignmentSearchTerm] = useState("");
  const [assignmentStatusFilter, setAssignmentStatusFilter] = useState<string>("all");
  const [assignmentDepartmentFilter, setAssignmentDepartmentFilter] = useState<string>("all");

  const [allAssignments, setAllAssignments] = useState<CombinedAssignmentWithDetails[]>([]);
  const [groupedAssignments, setGroupedAssignments] = useState<GroupedAssignment[]>([]);
  const [isLoadingAssignments, setIsLoadingAssignments] = useState(false);

  useEffect(() => {
    refreshPolicies();
    refreshMemos();
  }, []);

  useEffect(() => {
    const loadAssignments = async () => {
      setIsLoadingAssignments(true);
      try {
        const assignments = await getAllPolicyAssignments();
        setAllAssignments(assignments);
        const grouped = groupAssignmentsByDocument(assignments);
        setGroupedAssignments(grouped);
      } catch (error) {
        console.error('[PoliciesPage] Error loading assignments:', error);
      } finally {
        setIsLoadingAssignments(false);
      }
    };
    loadAssignments();
  }, []);

  const filteredPolicies = useMemo(() => {
    return policies && policies.filter(policy =>
      !policy.deleted &&
      (policy.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        policy.description.toLowerCase().includes(searchTerm.toLowerCase())) &&
      (statusFilter === "all" || policy.status === statusFilter)
    );
  }, [policies, searchTerm, statusFilter]);

  const filteredMemos = useMemo(() => {
    return memos && memos.filter(memo =>
      !memo.deleted &&
      (memo.subject.toLowerCase().includes(memoSearchTerm.toLowerCase()) ||
        memo.number.toLowerCase().includes(memoSearchTerm.toLowerCase())) &&
      (memoStatusFilter === "all" || memo.status === memoStatusFilter)
    );
  }, [memos, memoSearchTerm, memoStatusFilter]);

  const groupAssignmentsByDocument = (assignments: CombinedAssignmentWithDetails[]): GroupedAssignment[] => {
    const grouped = assignments.reduce((acc, assignment) => {
      const documentId = assignment.item.id;
      if (!acc[documentId]) {
        acc[documentId] = {
          id: documentId,
          type: assignment.type,
          name: assignment.item.title || assignment.item.subject || 'Unnamed Document',
          version: assignment.item.version,
          number: assignment.item.number,
          requireSignature: assignment.item.requireSignature,
          effectiveDate: assignment.item.effectiveDate,
          documentStatus: assignment.item.status,
          assignments: [],
          stats: { totalAssigned: 0, totalRead: 0, totalSigned: 0, completionPercentage: 0, overallStatus: 'Not Started' },
          departments: [],
          latestAssignedDate: '1970-01-01T00:00:00.000Z'
        };
      }
      acc[documentId].assignments.push(assignment);
      if (new Date(assignment.assignedDate) > new Date(acc[documentId].latestAssignedDate)) {
        acc[documentId].latestAssignedDate = assignment.assignedDate;
      }
      const userDepartment = assignment.user.department;
      if (userDepartment && !acc[documentId].departments.some(d => d.id === userDepartment.id)) {
        acc[documentId].departments.push(userDepartment);
      }
      return acc;
    }, {} as Record<string, GroupedAssignment>);

    return Object.values(grouped).map(group => {
      const { assignments, requireSignature } = group;
      const totalAssigned = assignments.length;
      if (totalAssigned === 0) return group;

      const totalRead = assignments.filter(a => !!a.readDate).length;
      const totalSigned = assignments.filter(a => !!a.signedDate).length;
      const totalOverdue = assignments.filter(a => a.status === 'Overdue').length;
      const totalCompleted = requireSignature ? totalSigned : totalRead;
      const completionPercentage = Math.round((totalCompleted / totalAssigned) * 100);

      let overallStatus: GroupedAssignment['stats']['overallStatus'] = 'Not Started';
      if (totalCompleted === totalAssigned) overallStatus = 'Complete';
      else if (totalOverdue > 0) overallStatus = 'Overdue';
      else if (totalCompleted > 0 || (requireSignature && totalRead > 0)) overallStatus = 'In Progress';

      group.stats = { totalAssigned, totalRead, totalSigned, completionPercentage, overallStatus };
      return group;
    }).sort((a, b) => new Date(b.latestAssignedDate).getTime() - new Date(a.latestAssignedDate).getTime());
  };

  const filteredGroupedAssignments = useMemo(() => {
    return groupedAssignments.filter(group => {
      const searchLower = assignmentSearchTerm.toLowerCase();
      const matchesSearch = assignmentSearchTerm === "" ||
        group.name.toLowerCase().includes(searchLower) ||
        group.assignments.some(a => a.user.name.toLowerCase().includes(searchLower) || a.user.email.toLowerCase().includes(searchLower));
      const matchesStatus = assignmentStatusFilter === "all" || group.stats.overallStatus === assignmentStatusFilter;
      const matchesDept = assignmentDepartmentFilter === "all" || group.departments.some(d => d.departmentCode === assignmentDepartmentFilter);
      return matchesSearch && matchesStatus && matchesDept;
    });
  }, [groupedAssignments, assignmentSearchTerm, assignmentStatusFilter, assignmentDepartmentFilter]);

  const getStatusBadge = (status: 'Draft' | 'Active' | 'Archived') => {
    const variants = { 'Draft': 'secondary', 'Active': 'default', 'Archived': 'outline' } as const;
    return <Badge variant={variants[status]}>{status}</Badge>;
  };

  const getAssignmentStatusBadge = (status: GroupedAssignment['stats']['overallStatus']) => {
    const styles = {
      'Complete': "bg-green-100 text-green-800",
      'In Progress': "bg-blue-100 text-blue-800",
      'Overdue': "bg-red-100 text-red-800",
      'Not Started': "bg-gray-100 text-gray-800",
    };
    return <Badge className={styles[status]}>{status}</Badge>;
  };

  // No local formatDate needed, using toSydneyDate from helpers



  const getTabSpecificButton = () => {
    const actions = {
      policies: (
        <Can I={ACTIONS.CREATE} a={RESOURCES.POLICY}>
          <Button onClick={() => navigate("/policies/new")} className="flex items-center gap-2 w-full md:w-auto"><Plus className="h-4 w-4" />New Policy</Button>
        </Can>
      ),
      memos: (
        <Can I={ACTIONS.CREATE} a={RESOURCES.MEMO}>
          <Button onClick={() => navigate("/memos/new")} className="flex items-center gap-2 w-full md:w-auto"><Plus className="h-4 w-4" />New Memo</Button>
        </Can>
      ),
      assignments: (
        <Can I={ACTIONS.CREATE} a={RESOURCES.POLICY_ASSIGNMENT}>
          <Button onClick={() => navigate("/policies/new-assign")} className="flex items-center gap-2 w-full md:w-auto"><Plus className="h-4 w-4" />New Assignment</Button>
        </Can>
      )
    }

    return actions[activeTab as keyof typeof actions];
  };

  return (
    <Can I={ACTIONS.READ} a={RESOURCES.POLICY} passThrough={
      <div className="p-4 md:p-6">
        <Card>
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="mb-2">Access Restricted</h3>
            <p className="text-muted-foreground">You don't have permission to view policies.</p>
          </CardContent>
        </Card>
      </div>
    }>
      <div className="space-y-6 p-2 md:p-0">
        <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold">Policies & Memos</h1>
            <p className="text-muted-foreground mt-1">Manage documents, track assignments, and view signatures.</p>
          </div>
          {getTabSpecificButton()}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card><CardContent className="p-4 flex items-center gap-3"><FileText className="h-8 w-8 text-blue-500" /><div><p className="text-sm text-muted-foreground">Policies</p><p className="text-2xl font-semibold">{policies.length}</p></div></CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-3"><FileText className="h-8 w-8 text-green-500" /><div><p className="text-sm text-muted-foreground">Active Memos</p><p className="text-2xl font-semibold">{memos.filter(m => m.status === 'Active').length}</p></div></CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-3"><Users className="h-8 w-8 text-purple-500" /><div><p className="text-sm text-muted-foreground">Assignments</p><p className="text-2xl font-semibold">{allAssignments.length}</p></div></CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-3"><CheckCircle className="h-8 w-8 text-orange-500" /><div><p className="text-sm text-muted-foreground">Signatures</p><p className="text-2xl font-semibold">{allAssignments.filter(a => !!a.signedDate).length}</p></div></CardContent></Card>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <div className="w-full overflow-x-auto">
            <TabsList className="bg-gray-100 p-1 rounded-full w-full md:w-fit">
              <TabsTrigger value="policies">Policies</TabsTrigger>
              <TabsTrigger value="memos">Memos</TabsTrigger>
              <TabsTrigger value="assignments">Assignments</TabsTrigger>
            </TabsList>
          </div>

          {/* Policies Tab */}
          <TabsContent value="policies" className="space-y-4 mt-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-col lg:flex-row gap-4">
                  <div className="flex-1 relative"><Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input placeholder="Search policies..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" /></div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}><SelectTrigger className="w-full lg:w-40"><SelectValue placeholder="Status" /></SelectTrigger><SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="Draft">Draft</SelectItem><SelectItem value="Active">Active</SelectItem><SelectItem value="Archived">Archived</SelectItem></SelectContent></Select>
                </div>
              </CardContent>
            </Card>

            {/* Mobile View: Card List */}
            <div className="space-y-4 md:hidden">
              {filteredPolicies.map((policy) => (
                <Card key={policy.id} onClick={() => navigate(`/policies/${policy.id}`)} className="cursor-pointer active:scale-[0.98]">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex justify-between items-start">
                      <h3 className="font-medium pr-2">{policy.title}</h3>
                      {getStatusBadge(policy.status)}
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">{policy.description}</p>
                    <div className="flex justify-between items-center text-sm pt-2 border-t">
                      <Badge variant="outline">v{policy.version || 1}</Badge>
                      <div className="flex items-center gap-1"><Calendar className="h-4 w-4 text-muted-foreground" /> {toSydneyDate(policy.effectiveDate)}</div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Desktop View: Table */}
            <Card className="hidden md:block">
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Status</TableHead><TableHead>Version</TableHead><TableHead>Signature</TableHead><TableHead>Effective Date</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {filteredPolicies.map((policy) => (
                      <TableRow key={policy.id} onClick={() => navigate(`/policies/${policy.id}`)} className="cursor-pointer">
                        <TableCell className="font-medium">{policy.title}</TableCell>
                        <TableCell>{getStatusBadge(policy.status)}</TableCell>
                        <TableCell><Badge variant="outline">v{policy.version || 1}</Badge></TableCell>
                        <TableCell>{policy.requireSignature ? <CheckCircle className="h-4 w-4 text-green-500" /> : <span className="text-muted-foreground">-</span>}</TableCell>
                        <TableCell>{toSydneyDate(policy.effectiveDate)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {filteredPolicies.length === 0 && (
              <div className="text-center py-8"><FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" /><h3 className="font-medium">No policies found</h3><p className="text-sm text-muted-foreground">Try adjusting your filters or create a new policy.</p></div>
            )}
          </TabsContent>

          {/* Memos Tab */}
          <TabsContent value="memos" className="space-y-4 mt-4">
            {/* Memos Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-col lg:flex-row gap-4">
                  <div className="flex-1 relative"><Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input placeholder="Search memos..." value={memoSearchTerm} onChange={(e) => setMemoSearchTerm(e.target.value)} className="pl-10" /></div>
                  <Select value={memoStatusFilter} onValueChange={setMemoStatusFilter}><SelectTrigger className="w-full lg:w-40"><SelectValue placeholder="Status" /></SelectTrigger><SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="Draft">Draft</SelectItem><SelectItem value="Active">Active</SelectItem><SelectItem value="Archived">Archived</SelectItem></SelectContent></Select>
                </div>
              </CardContent>
            </Card>

            {/* Mobile View: Card List */}
            <div className="space-y-4 md:hidden">
              {filteredMemos.map((memo) => (
                <Card key={memo.id} onClick={() => navigate(`/memos/${memo.id}`)} className="cursor-pointer active:scale-[0.98]">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex justify-between items-start">
                      <h3 className="font-medium pr-2">{memo.subject}</h3>
                      {getStatusBadge(memo.status)}
                    </div>
                    <div className="flex justify-between items-center text-sm pt-2 border-t">
                      <Badge variant="secondary">{memo.number}</Badge>
                      <div className="flex items-center gap-1"><Calendar className="h-4 w-4 text-muted-foreground" /> {toSydneyDate(memo.effectiveDate)}</div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Desktop View: Table */}
            <Card className="hidden md:block">
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow><TableHead>Number</TableHead><TableHead>Subject</TableHead><TableHead>Status</TableHead><TableHead>Version</TableHead><TableHead>Signature</TableHead><TableHead>Effective Date</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {filteredMemos.map((memo) => (
                      <TableRow key={memo.id} onClick={() => navigate(`/memos/${memo.id}`)} className="cursor-pointer">
                        <TableCell className="font-medium">{memo.number}</TableCell>
                        <TableCell>{memo.subject}</TableCell>
                        <TableCell>{getStatusBadge(memo.status)}</TableCell>
                        <TableCell><Badge variant="outline">v{memo.version || 1}</Badge></TableCell>
                        <TableCell>{memo.requireSignature ? <CheckCircle className="h-4 w-4 text-green-500" /> : <span className="text-muted-foreground">-</span>}</TableCell>
                        <TableCell>{toSydneyDate(memo.effectiveDate)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {filteredMemos.length === 0 && (
              <div className="text-center py-8"><FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" /><h3 className="font-medium">No memos found</h3><p className="text-sm text-muted-foreground">Try adjusting your filters or create a new memo.</p></div>
            )}
          </TabsContent>

          {/* Assignments Tab */}
          <TabsContent value="assignments" className="space-y-4 mt-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-col lg:flex-row gap-4">
                  <div className="flex-1 relative"><Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input placeholder="Search assignments..." value={assignmentSearchTerm} onChange={(e) => setAssignmentSearchTerm(e.target.value)} className="pl-10" /></div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Select value={assignmentStatusFilter} onValueChange={setAssignmentStatusFilter}><SelectTrigger className="w-full sm:w-auto"><SelectValue placeholder="Status" /></SelectTrigger><SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="Not Started">Not Started</SelectItem><SelectItem value="In Progress">In Progress</SelectItem><SelectItem value="Complete">Complete</SelectItem><SelectItem value="Overdue">Overdue</SelectItem></SelectContent></Select>
                    <Select value={assignmentDepartmentFilter} onValueChange={setAssignmentDepartmentFilter}><SelectTrigger className="w-full sm:w-auto"><SelectValue placeholder="Department" /></SelectTrigger><SelectContent><SelectItem value="all">All Departments</SelectItem>{departments.filter(d => !d.archived).map(d => <SelectItem key={d.id} value={d.departmentCode}>{d.departmentName}</SelectItem>)}</SelectContent></Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {isLoadingAssignments ? (
              <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div><p className="text-muted-foreground mt-2">Loading assignments...</p></div>
            ) : (
              <>
                {/* Mobile View: Card List */}
                <div className="space-y-4 md:hidden">
                  {filteredGroupedAssignments.map((group) => (
                    <Card key={group.id} onClick={() => navigate(`/policies/assign?${group.type === 'Policy' ? 'policyId' : 'memoId'}=${group.id}`)} className="cursor-pointer active:scale-[0.98]">
                      <CardContent className="p-4 space-y-3">
                        <div className="flex justify-between items-start gap-2">
                          <h3 className="font-medium pr-2 line-clamp-2">{group.name}</h3>
                          {getAssignmentStatusBadge(group.stats.overallStatus)}
                        </div>
                        <div className="text-sm text-muted-foreground space-y-2">
                          <div className="flex items-center gap-2">
                            <Badge variant={group.type === 'Policy' ? 'default' : 'secondary'}>{group.type}</Badge>
                            <div className="flex items-center gap-1.5"><Building2 className="h-4 w-4" /> {group.departments.length} Depts</div>
                          </div>
                          <div>
                            <Progress value={group.stats.completionPercentage} className="h-2" />
                            <div className="flex justify-between text-xs mt-1">
                              <span>{group.stats.completionPercentage}% complete</span>
                              <span>{group.requireSignature ? `${group.stats.totalSigned}/${group.stats.totalAssigned} Signed` : `${group.stats.totalRead}/${group.stats.totalAssigned} Read`}</span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Desktop View: Table */}
                <Card className="hidden md:block">
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Departments</TableHead><TableHead>Status</TableHead><TableHead>Completion</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {filteredGroupedAssignments.map((group) => (
                          <TableRow key={group.id} onClick={() => navigate(`/policies/assign?${group.type === 'Policy' ? 'policyId' : 'memoId'}=${group.id}`)} className="cursor-pointer">
                            <TableCell className="font-medium max-w-xs"><p className="truncate">{group.name}</p><span className="text-xs text-muted-foreground">{group.version ? `Version ${group.version}` : group.number}</span></TableCell>
                            <TableCell><Badge variant={group.type === 'Policy' ? 'default' : 'secondary'}>{group.type}</Badge></TableCell>
                            <TableCell className="max-w-xs"><p className="truncate">{group.departments.map(d => d.departmentCode).join(', ')}</p></TableCell>
                            <TableCell>{getAssignmentStatusBadge(group.stats.overallStatus)}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Progress value={group.stats.completionPercentage} className="w-24 h-2" />
                                <span className="text-xs text-muted-foreground">{group.requireSignature ? `${group.stats.totalSigned}/${group.stats.totalAssigned}` : `${group.stats.totalRead}/${group.stats.totalAssigned}`}</span>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </>
            )}
            {!isLoadingAssignments && filteredGroupedAssignments.length === 0 && (
              <div className="text-center py-8"><Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" /><h3 className="font-medium">No assignments found</h3><p className="text-sm text-muted-foreground">Try adjusting your filters or create a new assignment.</p></div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Can>
  );
}
